// Admin-only Kundenexport.
//
// Erzeugt pro Kunde einen druckfertigen Zeit-/Leistungsnachweis (PDF via
// Browser-Druck) ohne Stundensätze — direkt an den Kunden verschickbar.
// Zeitraum monatsweise (Default) oder frei wählbar. Rein lesend, keine Migration.
import { useState, useMemo } from 'react';
import { useData } from './DataContext.jsx';
import { durationMin, todayIso, pad } from './lib/format.js';
import {
  buildCustomerReport, renderReportHtml, printReportHtml,
  buildCustomerCsv, downloadCsv, hoursColon, safeName,
} from './lib/customer-export.js';
import { IconDownload, IconClock, IconBuilding, IconCheck, IconFolder } from './icons.jsx';

// { from, to } (ISO, inklusiv) für einen bestimmten Monats-Offset (0 = aktueller).
function monthRange(offset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() - offset; // kann negativ werden → Date normalisiert
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  return {
    from: `${first.getFullYear()}-${pad(first.getMonth() + 1)}-01`,
    to: `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`,
  };
}

function monthLabel(offset) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function rangeForPreset(preset, custom) {
  if (preset === 'thisMonth') return monthRange(0);
  if (preset === 'lastMonth') return monthRange(1);
  return { from: custom.from || null, to: custom.to || null };
}

export function CustomerExportPage() {
  const data = useData();
  const { isAdmin, timeEntries, projects, customers } = data;
  const [preset, setPreset] = useState('lastMonth');
  const [custom, setCustom] = useState({ from: '', to: todayIso() });
  const [customerId, setCustomerId] = useState('');
  const [done, setDone] = useState(null);
  const [warn, setWarn] = useState(null);

  const range = useMemo(() => rangeForPreset(preset, custom), [preset, custom]);

  const presets = [
    { key: 'thisMonth', label: `Aktueller Monat (${monthLabel(0)})` },
    { key: 'lastMonth', label: `Letzter Monat (${monthLabel(1)})` },
    { key: 'custom', label: 'Benutzerdefiniert' },
  ];

  // Stunden je Kunde im Zeitraum → Auswahl-Liste (nur Kunden mit Zeit zuerst).
  const customerRows = useMemo(() => {
    const projById = {};
    for (const p of projects) projById[p.id] = p;
    const minByCust = {};
    for (const te of timeEntries) {
      const d = te.entry_date;
      if (!d) continue;
      if (range.from && d < range.from) continue;
      if (range.to && d > range.to) continue;
      const proj = te.project_id ? projById[te.project_id] : null;
      const cid = te.customer_id || proj?.customer_id;
      if (!cid) continue;
      minByCust[cid] = (minByCust[cid] || 0) + durationMin(te.from_time, te.to_time);
    }
    return customers
      .map(c => ({ id: c.id, name: c.name, min: minByCust[c.id] || 0 }))
      .sort((a, b) => (b.min - a.min) || a.name.localeCompare(b.name, 'de'));
  }, [customers, projects, timeEntries, range]);

  const selected = customerRows.find(c => c.id === customerId) || null;

  const report = useMemo(() => {
    if (!customerId) return null;
    return buildCustomerReport({ timeEntries, projects, customers }, customerId, range);
  }, [customerId, timeEntries, projects, customers, range]);

  if (!isAdmin) {
    return (
      <div className="content">
        <div className="card card-pad" style={{ maxWidth: 480 }}>
          <div className="eyebrow">Kundenexport</div>
          <div style={{ marginTop: 8 }}>Dieser Bereich ist nur für Admins zugänglich.</div>
        </div>
      </div>
    );
  }

  const customInvalid = preset === 'custom' && custom.from && custom.to && custom.from > custom.to;
  const canExport = !!report && report.entryCount > 0 && !customInvalid;

  function handlePdf() {
    if (!report) return;
    setWarn(null);
    const logoUrl = `${window.location.origin}/assets/markenegger-logo.png`;
    const generatedLabel = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const html = renderReportHtml(report, { logoUrl, generatedLabel });
    const ok = printReportHtml(html);
    if (ok) setDone(`PDF-Druckansicht für „${selected?.name}" geöffnet – im Dialog „Als PDF sichern" wählen.`);
    else setWarn('Der Browser hat das Fenster blockiert. Bitte Pop-ups für diese Seite erlauben und erneut versuchen.');
  }

  function handleCsv() {
    if (!report) return;
    const stamp = (range.from || 'anfang') + '_' + (range.to || todayIso());
    downloadCsv(`zeitnachweis_${safeName(selected?.name)}_${stamp}.csv`, buildCustomerCsv(report));
    setDone(`CSV für „${selected?.name}" heruntergeladen.`);
  }

  return (
    <div className="content">
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow">Nachweis</div>
        <h1 className="serif" style={{ fontSize: 30, fontWeight: 500, margin: '4px 0 6px' }}>Kundenexport</h1>
        <div className="muted" style={{ fontSize: 14, maxWidth: 660 }}>
          Druckfertiger Zeit- / Leistungsnachweis pro Kunde — gruppiert nach Projekt, mit
          Einzelbuchungen und Summen. Ohne Stundensätze, direkt an den Kunden verschickbar.
        </div>
      </div>

      {/* Zeitraum */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Zeitraum</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: preset === 'custom' ? 12 : 0 }}>
          {presets.map(p => (
            <button key={p.key}
              className={`btn ${preset === p.key ? 'btn-primary' : ''}`}
              onClick={() => setPreset(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="tiny muted">Von</span>
              <input type="date" value={custom.from}
                onChange={e => setCustom(c => ({ ...c, from: e.target.value }))}
                className="input" style={{ colorScheme: 'dark' }}/>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="tiny muted">Bis</span>
              <input type="date" value={custom.to}
                onChange={e => setCustom(c => ({ ...c, to: e.target.value }))}
                className="input" style={{ colorScheme: 'dark' }}/>
            </label>
            {customInvalid && <span className="tiny" style={{ color: 'var(--crit)' }}>„Von" liegt nach „Bis".</span>}
          </div>
        )}
      </div>

      {/* Kundenauswahl */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Kunde</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 340, overflowY: 'auto' }}>
          {customerRows.map(c => {
            const active = c.id === customerId;
            return (
              <div key={c.id}
                onClick={() => { setCustomerId(c.id); setDone(null); setWarn(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px',
                  borderTop: '1px solid var(--stroke)', cursor: 'pointer', borderRadius: 8,
                  background: active ? 'var(--accent-soft, rgba(255,107,74,0.10))' : 'transparent',
                  opacity: c.min > 0 ? 1 : 0.5,
                }}>
                <div style={{ color: active ? 'var(--accent)' : 'var(--ink-3)' }}><IconBuilding size={16}/></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                  <div className="tiny muted">
                    {c.min > 0 ? `${hoursColon(c.min)} im Zeitraum` : 'keine Zeit im Zeitraum'}
                  </div>
                </div>
                {active && <IconCheck size={14} style={{ color: 'var(--accent)' }}/>}
              </div>
            );
          })}
          {customerRows.length === 0 && <div className="tiny muted" style={{ padding: 8 }}>Keine Kunden vorhanden.</div>}
        </div>
      </div>

      {/* Vorschau + Aktionen */}
      {report && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 200 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{selected?.name}</div>
              <div className="tiny muted">
                {report.groups.length} Projekt{report.groups.length === 1 ? '' : 'e'} ·
                {' '}{report.entryCount} Einträge · Gesamt <b style={{ color: 'var(--ink-1)' }}>{hoursColon(report.totalMin)}</b>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" disabled={!canExport} onClick={handleCsv}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <IconDownload size={12}/> CSV
              </button>
              <button className="btn btn-primary" disabled={!canExport} onClick={handlePdf}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <IconDownload size={14}/> PDF erstellen
              </button>
            </div>
          </div>

          {report.entryCount === 0 && (
            <div className="tiny muted" style={{ marginTop: 12 }}>
              Für diesen Kunden liegen im gewählten Zeitraum keine erfassten Zeiten vor.
            </div>
          )}

          {/* Kompakte Projekt-Übersicht */}
          {report.groups.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {report.groups.map(g => (
                <div key={g.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px',
                  borderTop: '1px solid var(--stroke)',
                }}>
                  <div style={{ color: 'var(--ink-3)' }}><IconFolder size={14}/></div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{g.title}</div>
                  <div className="tiny muted">{g.entries.length} Einträge</div>
                  <div className="mono" style={{ fontSize: 13, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                    {hoursColon(g.totalMin)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!report && (
        <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink-3)' }}>
          <IconClock size={16}/>
          <span className="tiny muted">Kunde auswählen, um den Nachweis zu erstellen.</span>
        </div>
      )}

      {done && (
        <div className="tiny" style={{ marginTop: 12, color: 'var(--ok)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconCheck size={12}/> {done}
        </div>
      )}
      {warn && (
        <div className="tiny" style={{ marginTop: 12, color: 'var(--crit)' }}>{warn}</div>
      )}
    </div>
  );
}
